import { computed, ref, toRefs, watch } from 'vue';
import {
  build_flattened_section_tabs,
  build_review_api,
  build_sync_review_state_handler,
  find_active_section_tab,
  normalize_form_sections
} from './application_details_review_helpers';

/**
 * @typedef {import('./application_details_review_helpers').ReviewFormSection} ReviewFormSection
 * @typedef {import('./application_details_review_helpers').ReviewQuestion} ReviewQuestion
 * @typedef {import('./application_details_review_helpers').ReviewSubSection} ReviewSubSection
 * @typedef {import('./application_details_review_helpers').ReviewSection} ReviewSection
 * @typedef {import('./application_details_review_helpers').ReviewSectionTab} ReviewSectionTab
 * @typedef {{ key: string, label: string, form_name: string, section: ReviewSection }} ReviewAccordionSection
 * @typedef {import('vue').Ref<string>} StringRef
 * @typedef {import('vue').Ref<string[]>} StringArrayRef
 * @typedef {import('vue').ComputedRef<ReviewFormSection[]>} ReviewFormSectionsComputedRef
 * @typedef {import('vue').ComputedRef<ReviewSectionTab[]>} ReviewSectionTabsComputedRef
 * @typedef {import('vue').ComputedRef<ReviewAccordionSection[]>} ReviewAccordionSectionsComputedRef
 */

function get_form_sections_prop(form_sections_ref) {
  return normalize_form_sections(form_sections_ref ? form_sections_ref.value : []);
}

/**
 * @param {unknown} value
 * @returns {ReviewSection}
 */
function normalize_review_section(value) {
  return value && typeof value === 'object' ? /** @type {ReviewSection} */ (value) : {};
}

/**
 * @param {unknown} value
 * @returns {ReviewQuestion}
 */
function normalize_review_question(value) {
  return value && typeof value === 'object' ? /** @type {ReviewQuestion} */ (value) : {};
}

/**
 * @param {ReviewFormSection} form_section
 * @returns {ReviewSection[]}
 */
function get_form_section_sections(form_section = {}) {
  const sections = form_section.sections;
  return Array.isArray(sections) ? sections.map(normalize_review_section) : [];
}

/**
 * @param {ReviewSubSection} sub_section
 * @returns {ReviewQuestion[]}
 */
function get_sub_section_questions(sub_section = {}) {
  const questions = sub_section.questions;
  return Array.isArray(questions) ? questions.map(normalize_review_question) : [];
}

/**
 * @param {ReviewFormSection[]} form_sections
 * @returns {ReviewAccordionSection[]}
 */
function build_accordion_sections(form_sections = []) {
  /** @type {ReviewAccordionSection[]} */
  const sections = [];
  form_sections.forEach((form_section) => {
    get_form_section_sections(form_section).forEach((section) => {
      sections.push({
        key: `${String(form_section.id || form_section.form_name || 'form')}:${String(section.id || section.label || 'section')}`,
        label: String(section.label || form_section.form_name || 'Section'),
        form_name: String(form_section.form_name || ''),
        section
      });
    });
  });
  return sections;
}

/**
 * @param {ReviewQuestion & { type?: string, file_name?: string, file_url?: string }} question
 * @returns {boolean}
 */
function question_has_document_answer(question = {}) {
  const question_type = String(question.type || '').trim().toLowerCase();
  return question_type === 'file' || Boolean(String(question.file_name || question.file_url || '').trim());
}

/**
 * @param {ReviewQuestion & { file_name?: string }} question
 * @returns {string}
 */
function question_document_value(question = {}) {
  return String(question.file_name || question.answer || 'Uploaded document').trim() || 'Uploaded document';
}

/**
 * @param {string[]} current_keys
 * @param {ReviewAccordionSection[]} next_sections
 * @returns {string[]}
 */
function sync_expanded_accordion_keys(current_keys = [], next_sections = []) {
  const next_keys = (Array.isArray(next_sections) ? next_sections : []).map((section) => section.key);
  const filtered_keys = current_keys.filter((key) => next_keys.includes(key));

  if (!filtered_keys.length && next_keys.length) {
    return next_keys.slice(0, 1);
  }

  return filtered_keys;
}

/**
 * @param {string} accordion_root_id
 * @param {import('vue').Ref<string[]>} expanded_accordion_keys
 * @returns {{
 *   accordion_header_id: (accordion_section?: ReviewAccordionSection) => string,
 *   is_accordion_open: (section_key?: string) => boolean,
 *   toggle_accordion: (section_key?: string) => void,
 *   question_document_label: (question?: ReviewQuestion & { file_name?: string }) => string,
 *   sub_section_has_document_answers: (sub_section?: ReviewSubSection) => boolean
 * }}
 */
function build_accordion_api(accordion_root_id, expanded_accordion_keys) {
  return {
    accordion_header_id(accordion_section = { key: '', label: '', form_name: '', section: {} }) {
      return `${accordion_root_id}-heading-${String(accordion_section.key || '').replace(/[^a-zA-Z0-9_-]+/g, '-')}`;
    },
    is_accordion_open(section_key = '') {
      return expanded_accordion_keys.value.includes(section_key);
    },
    toggle_accordion(section_key = '') {
      expanded_accordion_keys.value = expanded_accordion_keys.value.includes(section_key)
        ? expanded_accordion_keys.value.filter((key) => key !== section_key)
        : [section_key];
    },
    question_document_label(question = {}) {
      return question_document_value(question);
    },
    sub_section_has_document_answers(sub_section = {}) {
      const questions = get_sub_section_questions(sub_section);
      return questions.length > 0 && questions.every((question) => question_has_document_answer(question));
    }
  };
}

export default {
  name: 'ApplicationDetailsReview',
  props: {
    form_sections: {
      type: Array,
      default: () => []
    },
    layout: {
      type: String,
      default: 'sidebar'
    }
  },
  setup(props) {
    const { form_sections, layout } = toRefs(props);
    const accordion_root_id =
      `application-details-review-accordion-${Math.random().toString(36).slice(2, 10)}`;
    const normalized_form_sections = computed(() => get_form_sections_prop(form_sections));
    const uses_tabs_layout = computed(() => layout.value === 'tabs');
    const uses_accordion_layout = computed(() => layout.value === 'accordion');
    const flattened_section_tabs = computed(() => build_flattened_section_tabs(normalized_form_sections.value));
    const accordion_sections = computed(() => build_accordion_sections(normalized_form_sections.value));
    const active_section_key = /** @type {StringRef} */ (ref(''));
    const expanded_form_ids = /** @type {StringArrayRef} */ (ref(/** @type {string[]} */ ([])));
    const expanded_accordion_keys = /** @type {StringArrayRef} */ (ref(/** @type {string[]} */ ([])));
    const active_section_tab = computed(() =>
      find_active_section_tab(flattened_section_tabs.value, active_section_key.value)
    );
    const state = { active_section_key, expanded_form_ids };
    const review_api = build_review_api(state);
    const accordion_api = build_accordion_api(accordion_root_id, expanded_accordion_keys);

    watch(normalized_form_sections, build_sync_review_state_handler(state), { immediate: true });
    watch(
      accordion_sections,
      (next_sections) => {
        expanded_accordion_keys.value = sync_expanded_accordion_keys(
          expanded_accordion_keys.value,
          next_sections
        );
      },
      { immediate: true }
    );

    const setup_api ={
      active_section_key,
      active_section_tab,
      accordion_root_id,
      accordion_sections,
      expanded_form_ids,
      expanded_accordion_keys,
      flattened_section_tabs,
      normalized_form_sections,
      uses_accordion_layout,
      uses_tabs_layout,
      ...accordion_api,
      ...review_api
    };
    return setup_api;
  }
};
